// AI Generated Test
import { Component, OnInit } from '@angular/core';
import MonColors from '@monsido/colors/dist/es6/colors';
import { CookieBannerRepoService } from '../../services/cookie-banner-repo.service';
import moment from 'moment';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { COOKIE_CATEGORIES } from '@client/ng2/core/constants/cookie-categories.constant';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { CookieCategoriesLocalizeService } from '../../components/cookie-overview/categories-localize/cookie-categories-localize.service';

import { safeClone } from '@client/app/shared/utils/object-utils';
@Component({
    selector: 'cookie-acceptance-rate-overview',
    standalone: false,
    template: `
        <div id="cookie-acceptance-rate-overview">
            <div class="alert alert-warning font-size-1-3 font-weight-500 justify-content-center row" *ngIf="isDemo" translate>
                Consent results are sample data for demo purposes
            </div>
            <page-header
                [icon]="['SIDEMENUES', 'DATA_PRIVACY', 'CONSENT_OVERVIEW', 'COOKIE_BITE'] | monIcons"
                [header]="'Acceptance Rates' | translate"
                [subHeader]="subheaderTranslation">
                <page-header-content>
                    <statistics-date-picker
                            (onApply)="updateDateRange()"
                            [(date)]="date"
                            [allowToday]="true"
                            dateRangeGroup="consent-manager"
                            class="pull-right">
                    </statistics-date-picker>
                </page-header-content>
            </page-header>

            <div class="row mt-5 align-items-stretch">
                <div class="col-md-24 pb-2">
                    <div class="card panel-monsido-card mb-0 d-flex flex-column">
                        <div class="card-header">
                            <h2 class="inline-block font-weight-300"
                                translate>
                                Acceptance Rate by Category
                            </h2>
                            <span class="fas fa-question-circle py-1 inline-block ml-1"
                                  aria-hidden="true"
                                  [ngbTooltip]="'This chart shows how many percent of visitors accepted the various categories of cookies on your website. Visitors who did not make a consent choice on the cookie banner are not counted' | translate"
                                  [tooltipClass]="'tooltip-text-left'"
                                  [placement]="'right'"
                                  [container]="'body'">
                            </span>
                        </div>
                        <div class="card-body pt-0 flex-grow-1 row d-flex align-items-stretch">
                            <div class="col-48 py-2">
                                <mon-spinner [loading]="loading">
                                    <div class="row mt-3">
                                        <div class="col-48">
                                            <div class="no-result-message-container" *ngIf="categoriesArr.length === 0">
                                                <p class="d-flex justify-content-center font-weight-300 text-grey"
                                                >{{ 'No content was found' | translate }}</p>
                                            </div>
                                            <cookie-acceptance-rate
                                                *ngFor="let category of categoriesArr"
                                                [name]="category.name | capitalize"
                                                [percentage]="category.percentage">
                                            </cookie-acceptance-rate>
                                        </div>
                                    </div>
                                </mon-spinner>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-24">
                    <div class="card panel-monsido-card mb-6 d-flex flex-column">
                        <div class="card-header">
                            <h2 class="inline-block font-weight-300"
                                translate>
                                Acceptance Rate Over Time
                            </h2>
                        </div>
                        <div class="card-body pt-0 flex-grow-1 row d-flex align-items-stretch">
                            <div class="col-48 py-2">
                                <mon-spinner [loading]="loading">
                                        <div class="col-48">
                                            <mon-chart-line
                                                [height]="'300'"
                                                [data]="lineChartData"
                                                [series]="lineChartSeries"
                                                [labels]="lineChartLabels"
                                                [colors]="lineChartColors"
                                                [options]="lineChartOptions"
                                                [override]="lineChartOverride"
                                                [emptyDataMessage]="'No data available' | translate">
                                            </mon-chart-line>
                                        </div>
                                </mon-spinner>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class CookieAcceptanceRateOverviewComponent implements OnInit {
    loading: boolean = true;
    subheaderTranslation: string;
    date: any;
    isDemo: boolean = false;
    totalCount: number = 0;
    essentialCount: number = 0;
    categoriesArr: any[] = [];
    lineChartData: any = [[], [], [], []];
    lineChartLabels: any[] = [];
    lineChartSeries: any[];
    lineChartColors: any[] = [];
    lineChartOptions: any;
    lineChartOverride: any;

    constructor (
        private cookieBannerRepoService: CookieBannerRepoService,
        private dateTimeService: DateTimeService,
        private translateService: TranslateService,
        private paramService: ParamService,
        private demoHasAccessService: DemoHasAccessService,
        private dateRangeService: DateRangeService,
        private cookieCategoriesLocalizeService: CookieCategoriesLocalizeService,
    ) {
        this.subheaderTranslation = this.translateService.getString('An overview of your customer\'s behavior with cookies on your website');
        const basicString = this.translateService.getString('Basic');
        const marketingString = this.translateService.getString('Marketing');
        const preferencesString = this.translateService.getString('Preferences');
        const statisticsString = this.translateService.getString('Statistics');

        this.lineChartSeries = [basicString, preferencesString, marketingString, statisticsString];
    }

    ngOnInit (): void {
        this.setupLineChart();
        this.date = this.dateRangeService.setRangeDates({
            todayAsDefaultEnd: true,
        });
        this.updateDateRange();
        this.getStatisticsData();
    }

    updateDateRange (): void {
        const dateParams = this.paramService.returnPrevParam();
        if (!this.dateTimeService.isSame(dateParams.from, this.date.startDate) || !this.dateTimeService.isSame(dateParams.to, this.date.endDate)) {
            this.loading = true;
            this.getStatisticsData();
        }
        this.paramService.setParams({ from: moment(this.date.startDate), to: moment(this.date.endDate) });
    }

    getStatisticsData (): void {
        const request = {
            from: this.date.startDate,
            to: this.date.endDate,
        };
        this.isDemo = this.demoHasAccessService.isDemoConsentManager();
        if (this.isDemo) {
            this.updateStatisticsData(this.generateData());
            this.loading = false;
        } else {
            this.cookieBannerRepoService.getStatistics(request)
                .then((statistics) => {
                    this.updateStatisticsData(statistics);
                }, () => {})
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    updateStatisticsData (statistics: any): void {
        this.totalCount = statistics.total_count || 0;
        this.essentialCount = 0;
        const categoriesMap = this.getCategoriesMap(statistics.daily_consent_counts);
        const dailyCountMap = this.getDailyCountsMap(statistics.daily_consent_counts);
        this.categoriesArr = this.getCategoriesArr(categoriesMap)
            .filter((category) => {
                return category.name !== COOKIE_CATEGORIES.essential;
            })
            .map((entry) => this.updateNameTranslations(entry));
        this.categoriesArr = this.sortCategories(this.categoriesArr);
        this.setupLineChartData(dailyCountMap);
    }

    getCategoriesArr (categoryMap: Map<any, any>): any[] {
        const categoriesArr: any[] = [];
        this.essentialCount = categoryMap.get('essential')?.count || 0;
        if (categoryMap.size) {
            categoryMap.forEach((categoryObj) => {
                if (categoryObj) {
                    categoriesArr.push({
                        name: categoryObj.category,
                        percentage: this.essentialCount === 0 ? 0 : (categoryObj.count / this.essentialCount) * 100,
                        order: categoryObj.order,
                    });
                }
            });
        }
        return categoriesArr;
    }

    sortCategories (categoriesArr: any[]): any[] {
        return categoriesArr.slice().sort((a, b) => {
            const aOrder = a.order || 100;
            const bOrder = b.order || 100;

            if (aOrder < bOrder) {
                return -1;
            }

            if (aOrder > bOrder) {
                return 1;
            }

            return 0;
        });
    }

    setupLineChartData (dailyCountMap: Map<any, any>): void {
        this.setupLineChart();
        const sortedMap = this.getSortedMapByKey(dailyCountMap);
        const basicEntries: any[] = [];
        const marketingEntries: any[] = [];
        const preferencesEntries: any[] = [];
        const statisticsEntries: any[] = [];

        if (sortedMap.size) {
            sortedMap.forEach((dailyCountsArr, key, map) => {
                const dailyTotalCount = dailyCountsArr.find((obj: any) => {
                    return obj.consent === COOKIE_CATEGORIES.essential;
                });

                const newArr = dailyCountsArr
                    .map((obj: any) => {
                        if (obj.consent && obj.consent !== COOKIE_CATEGORIES.essential) {
                            let percentage = 0;
                            if (dailyTotalCount && dailyTotalCount.count !== 0) {
                                percentage = (obj.count * 100) / dailyTotalCount.count;
                            }

                            const updatedObj = safeClone(obj);
                            updatedObj.percentage = percentage.toFixed(2);

                            return updatedObj;
                        }

                        return null;
                    })
                    .filter((elem: any) => !!elem)
                    .sort((a: any, b: any) => {
                        if (a.order < b.order) {
                            return -1;
                        }
                        if (a.order > b.order) {
                            return 1;
                        }

                        return 0;
                    });

                map.set(key, newArr);
            });

            sortedMap.forEach((item, key) => {
                const basicItem = item.find((el: any) => {
                    return el.consent === COOKIE_CATEGORIES.basic;
                });

                basicEntries.push(basicItem ? basicItem.percentage : '0');

                const prefItem = item.find((el: any) => {
                    return el.consent === COOKIE_CATEGORIES.personalisation;
                });

                preferencesEntries.push(prefItem ? prefItem.percentage : '0');

                const markItem = item.find((el: any) => {
                    return el.consent === COOKIE_CATEGORIES.marketing;
                });

                marketingEntries.push(markItem ? markItem.percentage : '0');

                const statItem = item.find((el: any) => {
                    return el.consent === COOKIE_CATEGORIES.analytics;
                });

                statisticsEntries.push(statItem ? statItem.percentage : '0');

                this.lineChartLabels.push(this.dateTimeService.format(key,'MMM DD'));
            });

            this.lineChartData[0] = basicEntries;
            this.lineChartData[1] = preferencesEntries;
            this.lineChartData[2] = marketingEntries;
            this.lineChartData[3] = statisticsEntries;
        }
    }

    getSortedMapByKey (map: Map<any, any>): Map<any, any> {
        const mapKeys: any[] = [];
        const sortedMap = new Map();
        map.forEach((value, key) => {
            mapKeys.push(key);
        });

        mapKeys.sort((a, b) => {
            if (new Date(a).getTime() < new Date(b).getTime()) {
                return -1;
            }

            if (new Date(a).getTime() > new Date(b).getTime()) {
                return 1;
            }

            return 0;
        });

        mapKeys.forEach((key) => {
            sortedMap.set(key, map.get(key));
        });

        return sortedMap;
    }

    setupLineChart (): void {
        const visitorString = this.translateService.getString('Visitor percentage');

        this.lineChartData = [[], [], [], []];
        this.lineChartLabels = [];
        this.lineChartColors = [MonColors['branding-6'], MonColors['branding-4'], '#FDC50D', '#5E3EA8'];
        this.lineChartOptions = {
            scales: {
                yAxes: [
                    {
                        id: 'y-axis-0',
                        type: 'linear',
                        position: 'left',
                        grid: {
                            drawBorder: false,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: visitorString,
                            fontStyle: 400,
                        },
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            callback: (value: any) => {
                                return parseInt(value) + '%';
                            },
                            stepSize: 25,
                            min: 0,
                            max: 100,
                        },
                    },
                ],
                xAxes: [
                    {
                        id: 'x-axis-0',
                        gridLines: {
                            display: false,
                        },
                    },
                ],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    fontSize: 11,
                    boxWidth: 8,
                    boxHeight: 8,
                },
            },
            tooltips: {
                callbacks: {
                    label: (item: any, data: any) => {
                        const res = data.datasets[item.datasetIndex];
                        return res.label + ': ' + res.data[item.index] + '%';
                    },
                },
            },
        };

        this.lineChartOverride = [
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 4,
                borderWidth: 1,
                borderColor: MonColors['branding-6'],
                backgroundColor: MonColors['branding-6'],
                pointHitRadius: 10,
            },
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 4,
                borderWidth: 1,
                borderColor: MonColors['branding-4'],
                backgroundColor: MonColors['branding-4'],
                pointHitRadius: 10,
            },
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 4,
                borderWidth: 1,
                borderColor: '#FDC50D',
                backgroundColor: '#FDC50D',
                pointHitRadius: 10,
            },
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 4,
                borderWidth: 1,
                borderColor: '#5E3EA8',
                backgroundColor: '#5E3EA8',
                pointHitRadius: 10,
            },
        ];
    }

    getCategoriesMap (dailyConsentCounts: any[]): Map<any, any> {
        const categoriesOrderMap = this.getCategoriesOrderMap();
        if (dailyConsentCounts && dailyConsentCounts.length) {
            return dailyConsentCounts.reduce((acc, el) => {
                // only handle cases where consent is !== null (i.e 'essential')
                if (el.consent !== null) {
                    if (!acc.has(el.consent)) {
                        acc.set(el.consent, {
                            count: el.count,
                            category: el.consent,
                            order: categoriesOrderMap.get(el.consent).order,
                        });
                    } else {
                        const count = acc.get(el.consent).count;
                        acc.set(el.consent, {
                            count: count + el.count,
                            category: el.consent,
                            order: categoriesOrderMap.get(el.consent).order,
                        });
                    }
                }
                return acc;
            }, new Map());
        }
        return new Map();
    }

    // getting daily counts for calculating acceptance rate over time excluding essential cookies
    getDailyCountsMap (dailyConsentCounts: any[]): Map<any, any> {
        if (Array.isArray(dailyConsentCounts)) {
            return dailyConsentCounts.reduce((acc, el) => {
                // if (el.consent) {
                if (!acc.has(el.day)) {
                    acc.set(el.day, [{ count: el.count, day: el.day, consent: el.consent, order: this.getOrder(el.consent) }]);
                } else {
                    const elem = acc.get(el.day);
                    elem.push({
                        count: el.count,
                        day: el.day,
                        consent: el.consent,
                        order: this.getOrder(el.consent),
                    });
                    acc.set(el.day, elem);
                }
                // }
                return acc;
            }, new Map());
        }

        return new Map();
    }

    getOrder (consent: string): number {
        switch (consent) {
            case COOKIE_CATEGORIES.basic:
                return 1;
            case COOKIE_CATEGORIES.personalisation:
                return 2;
            case COOKIE_CATEGORIES.marketing:
                return 3;
            case COOKIE_CATEGORIES.analytics:
                return 4;
            default:
                return 5;
        }
    }

    getCategoriesOrderMap (): Map<any, any> {
        const map = new Map();
        map.set(COOKIE_CATEGORIES.basic, {
            order: 1,
            name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.basic],
        });
        map.set(COOKIE_CATEGORIES.personalisation, {
            order: 2,
            name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.personalisation],
        });
        map.set(COOKIE_CATEGORIES.marketing, {
            order: 3,
            name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.marketing],
        });
        map.set(COOKIE_CATEGORIES.analytics, {
            order: 4,
            name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.analytics],
        });
        map.set(COOKIE_CATEGORIES.essential, {
            order: 5,
            name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.essential],
        });

        return map;
    }

    generateData (): any {
        return {
            daily_total_counts: [
                { day: '2021-07-26', count: 210 },
                { day: '2021-07-27', count: 75 },
                { day: '2021-10-26', count: 210 },
                { day: '2021-11-27', count: 75 },
                { day: '2021-07-22', count: 210 },
                { day: '2021-12-27', count: 75 },
            ],
            daily_consent_counts: [
                { day: '2021-09-26', consent: 'marketing', count: 163 },
                { day: '2021-10-26', consent: 'personalisation', count: 163 },
                { day: '2021-11-26', consent: 'analytics', count: 163 },
                { day: '2021-07-26', consent: null, count: 163 },
                { day: '2021-07-26', consent: 'analytics', count: 18 },
                { day: '2021-07-26', consent: 'essential', count: 47 },
                { day: '2021-07-26', consent: 'marketing', count: 19 },
                { day: '2021-07-26', consent: 'personalisation', count: 19 },
                { day: '2021-07-27', consent: null, count: 20 },
                { day: '2021-07-27', consent: 'analytics', count: 3 },
                { day: '2021-07-27', consent: 'essential', count: 55 },
                { day: '2021-07-27', consent: 'marketing', count: 3 },
                { day: '2021-07-27', consent: 'personalisation', count: 3 },
            ],
            total_count: 285,
        };
    }

    updateNameTranslations (entry: any): any {
        entry.name = this.cookieCategoriesLocalizeService[entry.name] || entry.name;
        return entry;
    }
}
